import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import type { CalculateOffersResponse, Domain } from '@/services/inventory-api.service';
import { inventoryApi, type CalculateOffersPerson } from '@/services/inventory-api.service';
import { normalizeCalculateOffersResponse } from '@/utils/calculate-offers';

function parseDomainId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function splitBatchResponseByDomain(
  raw: CalculateOffersResponse,
  domainId: number,
  domainIndex: number,
  domainsCount: number,
  peoplePerDomainCount: number
): CalculateOffersResponse {
  const rawPeople = Array.isArray(raw.people) ? raw.people : undefined;
  const hasTaggedPeople = Boolean(
    rawPeople?.some((person) => parseDomainId(person.domain_id) != null)
  );

  let people = rawPeople;
  if (rawPeople) {
    if (hasTaggedPeople) {
      people = rawPeople.filter((person) => parseDomainId(person.domain_id) === domainId);
    } else if (
      peoplePerDomainCount > 0 &&
      rawPeople.length >= peoplePerDomainCount * domainsCount
    ) {
      const start = domainIndex * peoplePerDomainCount;
      const end = start + peoplePerDomainCount;
      people = rawPeople.slice(start, end);
    }
  }

  const rawItems = Array.isArray(raw.items) ? raw.items : [];
  const hasTaggedItems = rawItems.some((item) => parseDomainId(item.domain_id) != null);

  let items: CalculateOffersResponse['items'] = [];
  if (hasTaggedItems) {
    items = rawItems.filter((item) => parseDomainId(item.domain_id) === domainId);
  } else if (domainsCount === 1) {
    items = rawItems;
  }

  return {
    ...raw,
    items,
    people,
  };
}

export async function fetchDomainOffersEntries(
  domainIds: number[],
  people: CalculateOffersPerson[],
  selectedDomain?: Domain
): Promise<DomainOffersEntry[]> {
  if (domainIds.length === 0 || people.length === 0) return [];

  const hasTaggedPeople = people.some((person) => parseDomainId(person.domain_id) != null);
  const expandedPeople = hasTaggedPeople
    ? people
    : domainIds.flatMap((domainId) =>
        people.map((person) => ({
          ...person,
          domain_id: domainId,
        }))
      );

  const options = {
    partial_failures_allowed: true,
    domain_ids: domainIds,
  };

  const [calculateResRaw, domains] = await Promise.all([
    inventoryApi
      .calculateOffersBatch({ people: expandedPeople, options })
      .catch((): CalculateOffersResponse => ({ items: [], total: 0, currency: 'EUR' })),
    Promise.all(
      domainIds.map(async (domainId) => {
        if (selectedDomain?.pk === domainId) return selectedDomain;
        return inventoryApi.getDomainById(domainId);
      })
    ),
  ]);

  const domainsById = new Map<number, Domain>(domains.map((domain) => [domain.pk, domain]));

  const rawPeople = Array.isArray(calculateResRaw.people) ? calculateResRaw.people : undefined;
  const hasTaggedRawPeople = Boolean(
    rawPeople?.some((person) => parseDomainId(person.domain_id) != null)
  );

  const normalizedPeople =
    rawPeople && !hasTaggedRawPeople && rawPeople.length === expandedPeople.length
      ? rawPeople.map((person, index) => ({
          ...person,
          domain_id: parseDomainId(expandedPeople[index]?.domain_id),
        }))
      : rawPeople;

  const normalizedResponse: CalculateOffersResponse = {
    ...calculateResRaw,
    people: normalizedPeople,
  };

  const results = domainIds.map((domainId, domainIndex) => {
    const domain = domainsById.get(domainId) ?? ({ pk: domainId } as Domain);
    const domainResponse = splitBatchResponseByDomain(
      normalizedResponse,
      domainId,
      domainIndex,
      domainIds.length,
      people.length
    );
    const calculateRes = normalizeCalculateOffersResponse(domainResponse);
    const domainImage = calculateRes.image ?? domain.image;

    return {
      domain: {
        ...domain,
        image: domainImage,
      },
      offers: {
        items: calculateRes.items,
        total: calculateRes.total,
        currency: calculateRes.currency,
        people: calculateRes.people,
      },
    } satisfies DomainOffersEntry;
  });

  return results;
}
